import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Img } from "@/components/ui/img";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { toast } from "@/lib/toast";
import { Trash, FileText, CircleNotch, Copy, Check } from "@phosphor-icons/react";
import { formatMediaBytes, formatMediaDate } from "./utils";
import type { MediaItem } from "@/types";

export function MediaDetailDialog({
  item,
  isDeleting,
  onDelete,
  onClose,
}: {
  item: MediaItem;
  isDeleting: boolean;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const isImage = item.mimeType.startsWith("image/");
  const isVideo = item.mimeType.startsWith("video/");
  const previewUrl = item.urls.full || item.urls.medium || Object.values(item.urls)[0];

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch {
      toast.error("Kopiranje nije uspjelo.");
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={FileText}
          title={item.originalName}
          description={`${item.folder} · ${item.mimeType}`}
        />
      </DialogHeader>

      <DialogBody>
        <div className="flex flex-col gap-5">
          {/* Preview */}
          <div className="overflow-hidden rounded-xl border bg-muted/20">
            {isImage && previewUrl ? (
              <Img
                src={previewUrl}
                alt={item.originalName}
                className="mx-auto max-h-80 w-auto object-contain"
              />
            ) : isVideo && previewUrl ? (
              <video
                src={previewUrl}
                controls
                className="mx-auto max-h-80 w-auto"
              />
            ) : (
              <div className="flex h-40 items-center justify-center">
                <FileText className="size-16 text-muted-foreground/30" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Originalni naziv
              </p>
              <p className="mt-0.5 truncate text-foreground">
                {item.originalName}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Folder
              </p>
              <p className="mt-0.5 text-foreground">{item.folder}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Veličina
              </p>
              <p className="mt-0.5 text-foreground">{formatMediaBytes(item.size)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Tip</p>
              <p className="mt-0.5 text-foreground">{item.mimeType}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Uploadovao
              </p>
              <p className="mt-0.5 text-foreground">
                {item.uploaderName || item.uploadedBy}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Datum
              </p>
              <p className="mt-0.5 text-foreground">
                {formatMediaDate(item.createdAt)}
              </p>
            </div>
          </div>

          {/* URLs */}
          {Object.keys(item.urls).length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                URL adrese
              </p>
              <div className="space-y-1.5">
                {Object.entries(item.urls).map(([size, url]) => (
                  <div
                    key={size}
                    className="flex items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2"
                  >
                    <span className="shrink-0 text-xs font-medium text-muted-foreground">
                      {size}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                      {url}
                    </span>
                    <button
                      onClick={() => handleCopy(url)}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={`Kopiraj ${size} URL`}
                    >
                      {copiedUrl === url ? (
                        <Check className="size-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogBody>

      <DialogFooter>
        {confirmDelete ? (
          <div className="flex w-full items-center justify-between">
            <p className="text-sm font-medium text-rose-600">
              Da li ste sigurni?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(false)}
              >
                Otkaži
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <CircleNotch className="size-4 animate-spin" />
                    Brisanje...
                  </>
                ) : (
                  "Obriši"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash className="size-4" />
              Obriši
            </Button>
            <Button variant="outline" onClick={onClose}>
              Zatvori
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
